import { useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { PlatformOutletContext } from '../../app/platform-context.js'
import { platformOrganizationPath } from '../../app/platform-context.js'
import { ROUTES } from '../../app/routes.js'
import { listOrganizations } from '../../shared/api/organizations.js'
import { listPlatformMemberships } from '../../shared/api/platform-memberships.js'
import { listCities } from '../../shared/api/cities.js'
import { formatDate } from '../../shared/utils/format.js'
import type { OrganizationResponse } from '../../shared/types/models.js'
import { usePlatformApiError } from '../../app/usePlatformApiError.js'

interface OverviewData {
  organizationTotal: number | null
  administratorTotal: number | null
  cityTotal: number | null
  recentOrganizations: OrganizationResponse[]
  organizationsFailed: boolean
}

const EMPTY: OverviewData = {
  organizationTotal: null,
  administratorTotal: null,
  cityTotal: null,
  recentOrganizations: [],
  organizationsFailed: false,
}

export default function PlatformHomePage() {
  const { t, i18n } = useTranslation()
  const { platformRole } = useOutletContext<PlatformOutletContext>()
  const handleApiError = usePlatformApiError()
  const [data, setData] = useState<OverviewData>(EMPTY)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    let cancelled = false
    setStatus('loading')

    Promise.allSettled([
      listOrganizations({ page: 1, pageSize: 5 }),
      listPlatformMemberships({ page: 1, pageSize: 1 }),
      listCities({ page: 1, pageSize: 1 }),
    ]).then(([organizations, administrators, cities]) => {
      if (cancelled) return

      for (const result of [organizations, administrators, cities]) {
        if (result.status === 'rejected' && handleApiError(result.reason) === null) return
      }

      setData({
        organizationTotal: organizations.status === 'fulfilled' ? organizations.value.meta.total : null,
        administratorTotal: administrators.status === 'fulfilled' ? administrators.value.meta.total : null,
        cityTotal: cities.status === 'fulfilled' ? cities.value.meta.total : null,
        recentOrganizations:
          organizations.status === 'fulfilled' ? organizations.value.data : [],
        organizationsFailed: organizations.status === 'rejected',
      })
      setStatus([organizations, administrators, cities].every(result => result.status === 'rejected')
        ? 'error'
        : 'ready')
    })

    return () => {
      cancelled = true
    }
  }, [handleApiError])

  function count(value: number | null): string {
    return value === null ? '—' : String(value)
  }

  return (
    <section className="page-section page-section--wide">
      <div className="page-header">
        <div>
          <h1 className="platform-page-title">{t('platform.home.title')}</h1>
          <p className="page-section-description">{t('platform.home.subtitle')}</p>
        </div>
        <span className="platform-role-badge">{t(`platform.role.${platformRole}`)}</span>
      </div>

      {status === 'error' && (
        <div className="form-error-banner" role="alert">{t('platform.home.loadError')}</div>
      )}

      {status === 'loading' ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : (
        <>
          <div className="platform-stat-grid">
            <Link className="platform-stat-card" to={ROUTES.PLATFORM_ORGANIZATIONS}>
              <span className="platform-stat-label">{t('platform.home.organizations')}</span>
              <span className="platform-stat-value">{count(data.organizationTotal)}</span>
              <span className="platform-stat-hint">{t('platform.home.organizationsHint')}</span>
            </Link>
            <Link className="platform-stat-card" to={ROUTES.PLATFORM_ADMINISTRATORS}>
              <span className="platform-stat-label">{t('platform.home.administrators')}</span>
              <span className="platform-stat-value">{count(data.administratorTotal)}</span>
              <span className="platform-stat-hint">{t('platform.home.administratorsHint')}</span>
            </Link>
            <Link className="platform-stat-card" to={ROUTES.PLATFORM_CITIES}>
              <span className="platform-stat-label">{t('platform.home.cities')}</span>
              <span className="platform-stat-value">{count(data.cityTotal)}</span>
              <span className="platform-stat-hint">{t('platform.home.citiesHint')}</span>
            </Link>
          </div>

          <section className="settings-card platform-recent-card">
            <div className="page-header">
              <div>
                <h2>{t('platform.home.recentOrganizations')}</h2>
                <p className="page-section-description">{t('platform.home.recentOrganizationsHint')}</p>
              </div>
              <Link className="btn btn-ghost" to={ROUTES.PLATFORM_ORGANIZATIONS}>
                {t('platform.home.allOrganizations')}
              </Link>
            </div>

            {data.organizationsFailed ? (
              <div className="form-error-banner" role="alert">
                {t('platform.home.organizationsLoadError')}
              </div>
            ) : data.recentOrganizations.length === 0 ? (
              <p className="page-section-description">{t('platform.organizations.empty')}</p>
            ) : (
              <ul className="platform-recent-list">
                {data.recentOrganizations.map(organization => (
                  <li key={organization.id}>
                    <Link to={platformOrganizationPath(organization.id)}>
                      <span className="platform-recent-name">{organization.name}</span>
                      <span className="platform-recent-meta">
                        {organization.slug} · {t(`platform.organizationStatus.${organization.status}`)}
                      </span>
                    </Link>
                    <span className="platform-recent-date">
                      {formatDate(organization.createdAt, i18n.language)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </section>
  )
}
